/**
 * The only place coupon units are converted. The admin types rupees and
 * percent; the wire carries paise and basis points (1% = 100 bps).
 */
import { formatPrice } from '@/src/infrastructure/lib/currency';
import type { CouponSummary } from '../types';

export const MAX_PERCENTAGE_BPS = 10_000;

export const rupeesToPaise = (rupees: number) => Math.round(rupees * 100);
export const paiseToRupees = (paise: number) => paise / 100;
export const percentToBps = (percent: number) => Math.round(percent * 100);
export const bpsToPercent = (bps: number) => bps / 100;

type DiscountFields = Pick<CouponSummary, 'discountType' | 'discountValue' | 'maxDiscountPaise'>;

export function describeDiscount(c: DiscountFields): string {
  if (c.discountType === 'FLAT') return `${formatPrice(c.discountValue)} off`;
  const pct = `${bpsToPercent(c.discountValue)}% off`;
  return c.maxDiscountPaise === null ? pct : `${pct}, max ${formatPrice(c.maxDiscountPaise)}`;
}

/** `<input type="datetime-local">` value (browser-local time, minute precision) → UTC ISO. */
export const localInputToIso = (local: string) => new Date(local).toISOString();

/** UTC ISO → `<input type="datetime-local">` value in the browser's timezone. */
export function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const browserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The expiry sweep flips ACTIVE → EXPIRED every 5 minutes, so an ACTIVE row
 * can be past its window for a while; show that instead of a bare "ACTIVE".
 */
export function activeWindowHint(c: Pick<CouponSummary, 'status' | 'startAt' | 'endAt'>, now = Date.now()) {
  if (c.status !== 'ACTIVE') return null;
  if (new Date(c.endAt).getTime() < now) return 'ended';
  if (new Date(c.startAt).getTime() > now) return 'scheduled';
  return null;
}
